<script setup lang="ts">
import type { Schemas } from '#shopware'

interface CmsElementCategoryButtons {
    id: string
    type: 'category-buttons' | 'category_buttons'
    slot: string
    versionId?: string
    config: Record<string, never>
}

defineProps<{
    content: CmsElementCategoryButtons
}>()

const route = useRoute()
const localePath = useLocalePath()
const { locale, defaultLocale } = useI18n()

const navigationItems = inject<Ref<Schemas['NavigationRouteResponse'] | null>>('mainNavigation', ref(null))

const pathSegments = computed(() => {
    let path = route.path
    if (locale.value !== defaultLocale) {
        path = path.replace(new RegExp(`^/${locale.value}`), '') || '/'
    }
    return path.split('/').filter(Boolean)
})

const manufacturerSlug = computed(() => pathSegments.value[0] ?? '')

const manufacturerBase = computed(() => {
    const segments = pathSegments.value
    return segments.length > 0 ? `/${segments[0]}` : '/'
})

const currentSub = computed(() => {
    const segments = pathSegments.value
    return segments.length > 1 ? segments[1] : null
})

const mainCategories = computed<Schemas['Category'][]>(() =>
    (navigationItems.value as Schemas['Category'][] | null) ?? []
)

// Find the current category in the navigation tree by matching its own seoUrl to the slug
const currentCategory = computed<Schemas['Category'] | null>(() => {
    const slug = manufacturerSlug.value

    if (!slug) return null

    for (const cat of mainCategories.value) {
        // Check top-level category itself (e.g. /sale, /new)
        const topSeoPathInfo = (cat.seoUrls as Array<{ seoPathInfo: string }> | undefined)?.[0]?.seoPathInfo ?? ''
        if (topSeoPathInfo.replace(/\/$/, '').toLowerCase() === slug.toLowerCase()) {
            return cat
        }

        // Check children (e.g. manufacturer pages under /marken)
        const children = (cat.children as Schemas['Category'][]) ?? []
        const found = children.find(child => {
            const seoPathInfo = (child.seoUrls as Array<{ seoPathInfo: string }> | undefined)?.[0]?.seoPathInfo ?? ''
            return seoPathInfo.replace(/\/$/, '').toLowerCase() === slug.toLowerCase()
        })
        if (found) return found
    }
    return null
})
// Check if current category has an active child with that exact name
function isSubCategoryAvailable(subName: string): boolean {
    if (!currentCategory.value) return false
    const children = (currentCategory.value.children as Schemas['Category'][]) ?? []
    return children.some(c => c.active && c.name?.toLowerCase() === subName.toLowerCase())
}

const showDamenschuhe = computed(() => isSubCategoryAvailable('damenschuhe'))
const showHerrenschuhe = computed(() => isSubCategoryAvailable('herrenschuhe'))
const showKinderschuhe = computed(() => isSubCategoryAvailable('kinderschuhe'))

const isSpecialRootPage = computed(() => {
    const first = pathSegments.value[0]
    return (first === 'new' || first === 'sale') && pathSegments.value.length === 1
})

const showBestseller = computed(() => {
    if (!currentCategory.value) return false
    const marken = mainCategories.value.find(cat => cat.name === 'Marken')
    if (!marken) return false
    const children = (marken.children as Schemas['Category'][]) ?? []
    return children.some(child => child.id === currentCategory.value!.id)
})
</script>

<template>
    <div class="flex flex-wrap gap-3">
        <NuxtLink
            v-if="showDamenschuhe"
            :to="localePath(manufacturerBase + '/damenschuhe')"
            :class="['btn btn-medium', (currentSub === 'damenschuhe' || (isSpecialRootPage && currentSub === null)) ? 'btn-primary' : 'btn-secondary']"
        >
            {{ $t('categoryButtons.women') }}
        </NuxtLink>
        <NuxtLink
            v-if="showHerrenschuhe"
            :to="localePath(manufacturerBase + '/herrenschuhe')"
            :class="['btn btn-medium', currentSub === 'herrenschuhe' ? 'btn-primary' : 'btn-secondary']"
        >
            {{ $t('categoryButtons.men') }}
        </NuxtLink>
        <NuxtLink
            v-if="showKinderschuhe"
            :to="localePath(manufacturerBase + '/kinderschuhe')"
            :class="['btn btn-medium', currentSub === 'kinderschuhe' ? 'btn-primary' : 'btn-secondary']"
        >
            {{ $t('categoryButtons.kids') }}
        </NuxtLink>
        <NuxtLink
            v-if="showBestseller"
            :to="localePath(manufacturerBase)"
            :class="['btn btn-medium', currentSub === null ? 'btn-primary' : 'btn-secondary']"
        >
            {{ $t('categoryButtons.bestseller') }}
        </NuxtLink>
    </div>
</template>